import { Container } from "@mui/material";
import {
  SmartForm,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import { transform } from "ol/proj";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Coordinate } from "~/api/GeneratedApi";

type CoordinateForm = {
  lat: number | string;
  lon: number | string;
};

interface Props {
  coordinates?: Coordinate;
  onChange: (coords: Coordinate) => void;
}

const toWGS84 = (coordinates: Coordinate): Coordinate | null => {
  const tx = coordinates
    ? transform([coordinates.x, coordinates.y], "EPSG:3857", "EPSG:4326")
    : null;
  if (tx) return { x: tx[0], y: tx[1] };
  return null;
};

const fromWGS84 = (coordinates: Coordinate): Coordinate | null => {
  const tx = coordinates
    ? transform([coordinates.x, coordinates.y], "EPSG:4326", "EPSG:3857")
    : null;
  if (tx) return { x: tx[0], y: tx[1] };
  return null;
};

export const CoordinateSelector = ({
  coordinates,
  onChange,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const defaultValues = useMemo(() => {
    if (!coordinates) {
      return {
        lat: "",
        lon: "",
      };
    }
    const tx = toWGS84(coordinates);

    return {
      lat: tx?.x ?? "",
      lon: tx?.y ?? "",
    };
  }, [coordinates]);

  const methods = useSmartForm<CoordinateForm>({
    defaultValues,
  });

  return (
    <Container maxWidth={false}>
      <SmartForm<CoordinateForm>
        methods={methods}
        onSubmit={(d: CoordinateForm) => {
          if (
            d.lat &&
            d.lon &&
            typeof +d.lat === "number" &&
            typeof +d.lon === "number"
          ) {
            const tx = fromWGS84({ x: +d.lon, y: +d.lat });
            if (tx) onChange(tx);
          }
        }}
        gridProps={{
          justifyContent: "center",
          alignItems: "center",
          spacing: 1,
        }}
      >
        <SmartTextField<CoordinateForm>
          name="lon"
          type="number"
          label={t("map:wgs84-lon")}
          fullWidth
          variant="standard"
          gridProps={{ xs: 6, lg: 3 }}
          required
          inputProps={{ step: "any", title: "" }}
        />
        <SmartTextField<CoordinateForm>
          name="lat"
          type="number"
          label={t("map:wgs84-lat")}
          fullWidth
          variant="standard"
          gridProps={{ xs: 6, lg: 3 }}
          required
          inputProps={{ step: "any", title: "" }}
        />
        <SmartSubmit label={t("general:update")} gridProps={{ xs: 6, lg: 3 }} />
      </SmartForm>
    </Container>
  );
};
